import { toast } from '@heidi/design-system/toast';
import { AUDIO_FILE_EXTENSIONS } from '@heidi/utils';
import { useCallback, useMemo } from 'react';
import { type IntlShape, useIntl } from 'react-intl';

/**
 * Audio-extension set used by the audio short-circuit. Extensions are
 * lowercase, without the leading dot, to match `extensionOf(file)`.
 */
const AUDIO_EXTENSION_SET = new Set(
  AUDIO_FILE_EXTENSIONS.map((ext) => ext.replace(/^\./, '').toLowerCase())
);

const MAX_FILENAME_LENGTH = 32;

const truncateFileName = (name: string): string =>
  name.length <= MAX_FILENAME_LENGTH
    ? name
    : `${name.slice(0, MAX_FILENAME_LENGTH - 1)}…`;

const extensionOf = (file: File): string => {
  const dot = file.name.lastIndexOf('.');
  return dot >= 0 ? file.name.slice(dot + 1).toLowerCase() : '';
};

const toLowerCaseSet = (
  input: ReadonlySet<string> | readonly string[]
): ReadonlySet<string> => {
  if (input instanceof Set) return input as ReadonlySet<string>;
  return new Set((input as readonly string[]).map((s) => s.toLowerCase()));
};

/**
 * Optional handoff for audio files dropped on a non-audio surface. When
 * supplied, files whose extension is in `AUDIO_EXTENSION_SET` are surfaced
 * separately from other unsupported files with a friendlier toast that
 * points at the audio flow. When omitted, audio files are bucketed with
 * other unsupported files.
 */
interface AudioShortCircuit {
  /** Visible toast title; e.g. "Media files can't be attached here". */
  title: string;
  /** Visible toast body; e.g. "To transcribe audio or video, start a new session and add it from there.". */
  description: string;
}

export interface FileRejectionConfig {
  /**
   * Lowercase extensions (no dot) the surface accepts. The hook uses these
   * for client-side type validation so unsupported files never reach the
   * network.
   */
  allowedExtensions: ReadonlySet<string> | readonly string[];
  /**
   * Optional MIME-type allow-list checked **as a fallback** when the
   * extension doesn't match. Useful for surfaces that historically also
   * accepted files by MIME (e.g. a PDF dragged from an app that doesn't
   * preserve the `.pdf` extension but still sets `application/pdf`). Omit
   * to validate by extension only — the common case for greenfield
   * surfaces.
   */
  allowedMimeTypes?: ReadonlySet<string> | readonly string[];
  /**
   * Per-file size cap in bytes. Omit to skip size validation entirely (e.g.
   * the surface enforces size on the server only).
   */
  maxBytes?: number;
  /**
   * Short, already-localised description of what the surface accepts, used
   * inside the rejection toast body. Examples:
   *   - "PDF or Word documents"
   *   - "documents, spreadsheets, images, or plain text"
   */
  allowedFormatsLabel: string;
  /** Opt-in audio handoff. Omit to bucket audio with other unsupported files. */
  audioShortCircuit?: AudioShortCircuit;
}

interface FilePartitionResult {
  /** Files that passed every check; safe to upload. */
  accepted: File[];
  /** Files whose extension is not in `allowedExtensions` (excluding `audioRejected`). */
  unsupported: File[];
  /** Files that exceeded `maxBytes`. */
  oversized: File[];
  /** Audio files rejected via the audio short-circuit (subset of "would-be unsupported"). */
  audioRejected: File[];
}

const emptyPartition = (): FilePartitionResult => ({
  accepted: [],
  unsupported: [],
  oversized: [],
  audioRejected: [],
});

/**
 * Render the per-bucket toast for a partition result. Composes at most one
 * toast — the most actionable for the user.
 */
const composeRejectionToast = (
  result: FilePartitionResult,
  args: {
    allowedFormatsLabel: string;
    maxBytes: number | undefined;
    audioShortCircuit: AudioShortCircuit | undefined;
  },
  intl: IntlShape
): void => {
  const { unsupported, oversized, audioRejected } = result;
  const { allowedFormatsLabel, maxBytes, audioShortCircuit } = args;

  // Precedence (one toast, most-actionable first):
  //   audio short-circuit > unsupported > oversized.
  // Each branch `return`s after firing, so reordering the blocks below
  // changes which bucket "wins" when multiple rejection types coexist.
  if (audioRejected.length > 0 && audioShortCircuit) {
    toast({
      tone: 'destructive',
      title: audioShortCircuit.title,
      description: audioShortCircuit.description,
    });
    return;
  }

  if (unsupported.length === 1) {
    const file = unsupported[0]!;
    toast({
      tone: 'destructive',
      title: intl.formatMessage(
        {
          defaultMessage: '{name} isn’t supported',
          id: 'grEoem3m99',
        },
        { name: truncateFileName(file.name) }
      ),
      description: intl.formatMessage(
        {
          defaultMessage: 'Heidi accepts {allowedFormats}.',
          id: 'LRwkWBhnZM',
        },
        { allowedFormats: allowedFormatsLabel }
      ),
    });
    return;
  }

  if (unsupported.length > 1) {
    toast({
      tone: 'destructive',
      title: intl.formatMessage(
        {
          defaultMessage: '{count} files aren’t supported',
          id: 'adXu8oUyTo',
        },
        { count: unsupported.length }
      ),
      description: intl.formatMessage(
        {
          defaultMessage: 'Heidi accepts {allowedFormats}.',
          id: 'LRwkWBhnZM',
        },
        { allowedFormats: allowedFormatsLabel }
      ),
    });
    return;
  }

  if (oversized.length > 0) {
    const maxMb =
      typeof maxBytes === 'number' ? Math.round(maxBytes / (1024 * 1024)) : null;

    if (oversized.length === 1) {
      const file = oversized[0]!;
      toast({
        tone: 'destructive',
        title: intl.formatMessage(
          {
            defaultMessage: '{name} is too large',
            id: 'z9zPRNawGX',
          },
          { name: truncateFileName(file.name) }
        ),
        description:
          maxMb !== null
            ? intl.formatMessage(
                {
                  defaultMessage: 'Files must be under {maxMb} MB.',
                  id: 'Nxh4t5Pf0H',
                },
                { maxMb }
              )
            : undefined,
      });
    } else {
      toast({
        tone: 'destructive',
        title: intl.formatMessage(
          {
            defaultMessage: '{count} files are too large',
            id: '+Nh8+1sPSj',
          },
          { count: oversized.length }
        ),
        description:
          maxMb !== null
            ? intl.formatMessage(
                {
                  defaultMessage: 'Files must be under {maxMb} MB.',
                  id: 'Nxh4t5Pf0H',
                },
                { maxMb }
              )
            : undefined,
      });
    }
  }
};

/**
 * Centralised client-side file rejection — extension allow-list, optional
 * MIME fallback, optional per-file size cap, optional audio short-circuit.
 *
 * Surfaces a single, localised, friendly toast per rejection (never the
 * raw backend error, never a 15-extension dump). Returns the accepted
 * subset so callers can keep the upload pipeline unchanged.
 *
 * The hook depends on individual primitive `config` fields rather than the
 * whole object, so a caller doesn't need to `useMemo` the config — passing
 * a fresh object literal each render is fine as long as the *fields*
 * inside it are referentially stable (strings interned by `intl`,
 * top-level constants, etc.).
 *
 * Usage:
 *
 * ```ts
 * const { partitionAndReport } = useFileRejectionToast({
 *   allowedExtensions: CHAT_ATTACHMENTS_ALLOWED_EXTENSIONS,
 *   maxBytes: CHAT_ATTACHMENT_MAX_SIZE_BYTES,
 *   allowedFormatsLabel: intl.formatMessage(messages.allowedFormatsLabel),
 *   audioShortCircuit: {
 *     title: intl.formatMessage(messages.audioTitle),
 *     description: intl.formatMessage(messages.audioDescription),
 *   },
 * });
 *
 * const accepted = partitionAndReport(droppedFiles);
 * ```
 */
export function useFileRejectionToast(config: FileRejectionConfig) {
  const intl = useIntl();
  const {
    allowedExtensions,
    allowedMimeTypes,
    maxBytes,
    allowedFormatsLabel,
    audioShortCircuit,
  } = config;

  const allowedSet = useMemo(
    () => toLowerCaseSet(allowedExtensions),
    [allowedExtensions]
  );

  const allowedMimeSet = useMemo(
    () => (allowedMimeTypes ? toLowerCaseSet(allowedMimeTypes) : null),
    [allowedMimeTypes]
  );

  // Depend on the two primitive fields rather than the `audioShortCircuit`
  // object reference, so a caller passing a fresh `{ title, description }`
  // literal each render doesn't churn callback identities.
  const audioTitle = audioShortCircuit?.title;
  const audioDescription = audioShortCircuit?.description;
  const hasAudioHandoff = audioTitle !== undefined && audioDescription !== undefined;

  const partition = useCallback(
    (files: File[]): FilePartitionResult => {
      const result = emptyPartition();
      if (files.length === 0) return result;

      for (const file of files) {
        const ext = extensionOf(file);
        // Extension is the primary check; MIME is a fallback so files
        // with a valid type but a missing/odd extension (e.g. a PDF
        // dragged from an app that drops the `.pdf`) are still accepted
        // when the surface opts into `allowedMimeTypes`.
        const matchesExt = allowedSet.has(ext);
        const matchesMime =
          !matchesExt && allowedMimeSet !== null && file.type
            ? allowedMimeSet.has(file.type.toLowerCase())
            : false;

        if (!matchesExt && !matchesMime) {
          if (hasAudioHandoff && AUDIO_EXTENSION_SET.has(ext)) {
            result.audioRejected.push(file);
          } else {
            result.unsupported.push(file);
          }
          continue;
        }

        if (typeof maxBytes === 'number' && file.size > maxBytes) {
          result.oversized.push(file);
          continue;
        }

        result.accepted.push(file);
      }

      return result;
    },
    [allowedSet, allowedMimeSet, hasAudioHandoff, maxBytes]
  );

  const showRejectionToast = useCallback(
    (result: FilePartitionResult) =>
      composeRejectionToast(
        result,
        {
          allowedFormatsLabel,
          maxBytes,
          audioShortCircuit:
            audioTitle !== undefined && audioDescription !== undefined
              ? { title: audioTitle, description: audioDescription }
              : undefined,
        },
        intl
      ),
    [allowedFormatsLabel, maxBytes, audioTitle, audioDescription, intl]
  );

  const partitionAndReport = useCallback(
    (files: File[]): File[] => {
      const result = partition(files);
      showRejectionToast(result);
      return result.accepted;
    },
    [partition, showRejectionToast]
  );

  return { partition, showRejectionToast, partitionAndReport };
}
